import * as React from "react"
import { cn } from "../../lib/utils"
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle } from "lucide-react"

const ToastContext = React.createContext()

export const useToast = () => {
  const context = React.useContext(ToastContext)
  if (!context) {
    throw new Error("useToast must be used within ToastProvider")
  }
  return context
}

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = React.useState([])

  const showToast = (message, type = "info", duration = 5000) => {
    const id = Date.now().toString()
    const toast = { id, message, type, duration }
    
    setToasts((prev) => {
      // 限制最多显示 5 条 Toast
      const newToasts = [...prev, toast]
      if (newToasts.length > 5) {
        // 移除最旧的 Toast
        return newToasts.slice(-5)
      }
      return newToasts
    })
    
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }
    
    return id
  }

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }

  const success = (message, duration = 5000) => showToast(message, "success", duration)
  const error = (message, duration = 5000) => showToast(message, "error", duration)
  const info = (message, duration = 5000) => showToast(message, "info", duration)
  const warning = (message, duration = 5000) => showToast(message, "warning", duration)

  return (
    <ToastContext.Provider value={{ success, error, info, warning, removeToast }}>
      {children}
      <div 
        className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-[calc(100vw-2rem)] sm:max-w-md"
        style={{
          maxHeight: 'calc(100vh - 2rem)',
          overflowY: 'auto',
          paddingRight: 'env(safe-area-inset-right)',
          paddingTop: 'max(1rem, env(safe-area-inset-top))',
        }}
      >
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

const Toast = ({ message, type, onClose }) => {
  const icons = {
    success: CheckCircle2,
    error: AlertCircle,
    info: Info,
    warning: AlertTriangle,
  }

  const styles = {
    success: "bg-green-500 text-white border-green-600",
    error: "bg-red-500 text-white border-red-600",
    info: "bg-blue-500 text-white border-blue-600",
    warning: "bg-yellow-500 text-white border-yellow-600",
  }

  const Icon = icons[type] || Info

  return (
    <div
      className={cn(
        "flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg shadow-lg border animate-in slide-in-from-right",
        "min-w-[280px] max-w-full",
        styles[type]
      )}
      style={{
        minHeight: '44px', // iOS 最小触摸目标
      }}
    >
      <Icon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
      <p className="flex-1 text-xs sm:text-sm font-medium break-words">{message}</p>
      <button
        type="button"
        onClick={onClose}
        className="flex-shrink-0 hover:opacity-80 transition-opacity min-w-[44px] min-h-[44px] flex items-center justify-center"
        aria-label="关闭通知"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}


